'use client'

import { useRef, useState } from 'react'

import { toast } from 'sonner'

import { useCampaignJoinFlow } from '../hooks/useCampaignJoinFlow'
import { mapCampaignJoinErrorMessage } from '../lib/campaign-join-errors'
import { CAMPAIGN_PAGE_VARIANT, formatCampaignPeriod } from '../lib/campaign-ui'

import { useAppRouter } from '@/shared/lib/router'
import { Button } from '@/shared/ui/Button'
import { inputClassName } from '@/shared/ui/input-styles'
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@/shared/ui/Modal'

import { CampaignListSkeleton } from './CampaignListSkeleton'

interface CampaignJoinByCodeModalProps {
  onHide: () => void
}

export function CampaignJoinByCodeModal({ onHide }: CampaignJoinByCodeModalProps) {
  const router = useAppRouter()
  const [codeInput, setCodeInput] = useState('')
  const [submittedCode, setSubmittedCode] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const hasConfirmed = useRef(false)

  const { previewQuery, joinMutation } = useCampaignJoinFlow(submittedCode, {
    enabled: submittedCode.length > 0
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = codeInput.trim()
    if (!trimmed) {
      setValidationError('Informe o código da campanha.')
      return
    }
    setValidationError(null)
    setSubmittedCode(trimmed)
  }

  const handleRetry = () => {
    setSubmittedCode('')
    hasConfirmed.current = false
  }

  const handleConfirm = () => {
    hasConfirmed.current = true
    joinMutation.mutate(undefined, {
      onSuccess: record => {
        toast.success(
          record.already_joined
            ? 'Você já está inscrito nesta campanha.'
            : 'Inscrição confirmada!'
        )
        onHide()
        router.push(`/campanhas/${record.campaign_id}`)
      },
      onError: error => {
        hasConfirmed.current = false
        toast.error(mapCampaignJoinErrorMessage(error))
      }
    })
  }

  const previewErrorMessage = previewQuery.isError
    ? mapCampaignJoinErrorMessage(previewQuery.error)
    : null
  const campaign = previewQuery.data?.campaign

  return (
    <Modal asCard variant={CAMPAIGN_PAGE_VARIANT}>
      <ModalHeader>
        <ModalTitle>Entrar com código</ModalTitle>
      </ModalHeader>
      <ModalBody className="space-y-4">
        {!submittedCode || previewErrorMessage ? (
          <>
            <p className="text-sm text-[#7F7FAD]">
              Peça o código de convite para o professor responsável pela campanha e
              digite abaixo.
            </p>
            <form onSubmit={handleSearch} className="flex flex-col gap-2">
              <input
                value={codeInput}
                onChange={e => setCodeInput(e.target.value)}
                placeholder="Ex.: PROFBRUNO"
                autoComplete="off"
                className={inputClassName}
              />
              {(validationError || previewErrorMessage) && (
                <p className="text-sm font-medium text-destructive">
                  {validationError ?? previewErrorMessage}
                </p>
              )}
              <Button type="submit" disabled={previewQuery.isFetching}>
                {previewQuery.isFetching ? 'Buscando...' : 'Buscar'}
              </Button>
            </form>
          </>
        ) : previewQuery.isLoading ? (
          <CampaignListSkeleton />
        ) : campaign ? (
          <div className="space-y-2 text-center">
            <h2 className="font-sans text-xl font-bold text-[#974319]">
              {campaign.name}
            </h2>
            <p className="text-sm font-medium text-[#7F7FAD]">
              {formatCampaignPeriod(campaign.starts_at, campaign.ends_at)}
            </p>
          </div>
        ) : null}
      </ModalBody>
      {campaign && !previewErrorMessage && (
        <ModalFooter className="flex-col gap-2">
          <Button
            className="w-full"
            disabled={joinMutation.isPending || hasConfirmed.current}
            onClick={handleConfirm}
          >
            {joinMutation.isPending ? 'Confirmando...' : 'Confirmar ingresso'}
          </Button>
          <Button variant="outline" className="w-full" onClick={handleRetry}>
            Buscar outro código
          </Button>
        </ModalFooter>
      )}
    </Modal>
  )
}
